import { TUserInfo } from "@features/reducer.info";
import React from "react";
import { Card, LightPurpleCard } from "../../Card/Card";
import arrowIcon from "../../../images/arrow-black.svg?url";
import { kycStatus } from "./const";
import kycIcon from "./images/kyc-icon.png";
import accountTypeIcon from "./images/account-type-icon.png";
import btcWealthBuilderIcon from "./images/bitcoin-wealth-builder-icon.svg?url";
import bfIcon from "./images/bf.png";
import trackProgressIcon from "./images/track-progress.png";
import { formatNumberToUSD } from "@features/utils/functions";
import { useMembershipPageContext } from "@profile/pages/MembershipPage/MembershipPage.context";
import { LinkButton, SimpleTextButton } from "../../Button/Button";
import { AccountTier } from "@bundles/consts";
import { TierBadge, tierLabels } from "@appComponents/TierBadge/TierBadge";
import { UnlockBonusesCardContent } from "@appComponents/UnlockBonusesCardContent/UnlockBonusesCardContent";

type TopSectionProps = {
  userInfo: TUserInfo;
  className?: string;
};

export const TopSection = ({ userInfo, className }: TopSectionProps) => {
  const {
    timelockMinValue,
    setInvestmentPlatformAccordionOpen,
    setIsTrackProgressAccordionOpen,
    handleChangeActiveTrackTab,
  } = useMembershipPageContext();

  const progressPercentage = Math.min(
    (userInfo.timelock_balance ?? 0 / Number(timelockMinValue)) * 100,
    100
  );

  const isUk = userInfo.country === "GB";

  const kycStatusLabel =
    kycStatus[userInfo.verification_status as keyof typeof kycStatus];

  return (
    <div className={className}>
      <div className="flex flex-col md:flex-row justify-between md:items-center mb-12">
        <div className="flex items-center gap-[8px]">
          <p className="mb-0 text-xl font-semibold">{userInfo.full_name}</p>
          <TierBadge tier={userInfo.tier as AccountTier} />
        </div>

        <div className="text-sm">
          <span className="font-bold">Account:&nbsp;</span>
          {userInfo?.organizational ? "Organizational" : "Individual"}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="flex gap-[15px]">
            <img src={kycIcon} className="h-[44px]" alt="kyc icon" />

            <div className="flex md:flex-col flex-row justify-between md:justify-start flex-1 md:items-start items-end">
              <div className="md:mb-[32px]">
                <div className="font-medium mb-2">ID Verification</div>
                <div>
                  <span className="text-sm font-medium">Status:&nbsp;</span>
                  <span
                    className="inline-flex items-center gap-[3px]"
                    style={{
                      color: kycStatusLabel.color,
                    }}
                  >
                    {kycStatusLabel?.label}
                    {kycStatusLabel?.icon && (
                      <img
                        src={kycStatusLabel.icon as string}
                        alt="Status label icon"
                      />
                    )}
                  </span>
                </div>
              </div>

              <SimpleTextButton href={process.env.BF_IDENTITY_URL}>
                Update
                <img className="ml-2" src={arrowIcon} alt="arrow" />
              </SimpleTextButton>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex gap-[15px]">
            <img src={accountTypeIcon} className="h-[44px]" alt="kyc icon" />

            <div className="flex md:flex-col flex-row justify-between md:justify-start flex-1 md:items-start items-end">
              <div className="md:mb-[32px]">
                <div className="font-medium mb-2">Account type</div>
                <div data-cy="membership-page-tier-status" className="text-sm">
                  <span className="font-medium">Status:&nbsp;</span>
                  <span>{tierLabels[userInfo.tier as AccountTier]}</span>
                </div>
              </div>

              <SimpleTextButton
                href={`${process.env.BF_IDENTITY_URL}account-type`}
              >
                {(userInfo.tier === AccountTier.Tier_4 && userInfo.hnw) ||
                (!userInfo.hnw &&
                  (userInfo.tier === AccountTier.Tier_2 ||
                    userInfo.tier === AccountTier.Tier_3))
                  ? "View"
                  : "Upgrade"}
                <img className="ml-2" src={arrowIcon} alt="arrow" />
              </SimpleTextButton>
            </div>
          </div>
        </Card>

        <Card>
          <UnlockBonusesCardContent
            isUk={isUk}
            timelockMinValue={timelockMinValue}
            timeLockBalance={userInfo.timelock_balance}
            actions={
              <div>
                <LinkButton
                  onClick={() => {
                    setIsTrackProgressAccordionOpen(true);
                    if (userInfo.bitcoin_journey_step >= 3) {
                      handleChangeActiveTrackTab("track_2");
                    } else {
                      handleChangeActiveTrackTab("track_1");
                    }

                    setTimeout(() => {
                      document
                        .getElementById("track-progress-steps")
                        ?.scrollIntoView({ behavior: "smooth" });
                    }, 100);
                  }}
                >
                  {(userInfo.timelock_balance ?? 0) < Number(timelockMinValue)
                    ? "Unlock"
                    : "View"}
                </LinkButton>
              </div>
            }
          />
        </Card>
      </div>

      <div className="mx-auto max-w-[525px] py-[32px] my-[48px] text-center">
        <h3>Welcome to Bnk To The Future!</h3>
        <div>
          You’ve reached the starting point for building and managing your
          Bitcoin wealth. This page will guide you through the initial setup
          process.
        </div>
      </div>

      <div>
        <h5>Getting started</h5>
        <Card className="mt-6 md:flex flex-col md:flex-row justify-between">
          <div className="flex gap-6">
            <img
              src={btcWealthBuilderIcon}
              className="h-[44px]"
              alt="Bitcoin Wealth Builder icon"
            />

            <div className="max-w-[770px]">
              <div className="font-semibold">
                Bitcoin Wealth Builder Program
              </div>
              <div>
                {isUk
                  ? "Apply to get the complimentary Bitcoin Wealth Builder Program, available to members who meet the criteria in the  “Track Your Progress” section below. This program offers all the essential tools to help you build and manage your Bitcoin wealth through our All-In-One Bitcoin Investment Platform."
                  : "Begin by qualifying for our exclusive Bitcoin Wealth Builder Program, valued at $5,000 for FREE, available to members who meet the criteria in the “Track Your Progress” section below. This program offers all the essential tools to help you build and manage your Bitcoin wealth through our All-In-One Bitcoin Investment Platform."}
              </div>
            </div>
          </div>

          <div className="mt-8 md:mt-0">
            <LinkButton
              onClick={() => {
                setInvestmentPlatformAccordionOpen(true);

                setTimeout(() => {
                  document
                    .getElementById("ready-to-get-the-bitcoin-container")
                    ?.scrollIntoView({ behavior: "smooth" });
                }, 100);
              }}
            >
              Explore
            </LinkButton>
          </div>
        </Card>
      </div>

      <div className="mt-[48px]">
        <h5>What You’ll Find on This Page</h5>

        <div className="flex flex-col md:flex-row gap-8 mt-6">
          <LightPurpleCard className="flex flex-1 gap-8 pb-16">
            <img src={bfIcon} className="h-[50px]" alt="bf icon" />

            <div>
              <div className="font-semibold">
                Using our All-In-One Bitcoin Investment Platform
              </div>

              <div>
                An introduction to the services we offer to build and manage
                your Bitcoin wealth.
              </div>
            </div>
          </LightPurpleCard>

          <LightPurpleCard className="flex flex-1 gap-8 pb-16">
            <img
              src={trackProgressIcon}
              className="h-[50px]"
              alt="track progress icon"
            />

            <div>
              <div className="font-semibold">Track Your Progress</div>

              <div>
                {isUk
                  ? "A tracking tool to “Track Your Progress”, which aims to ensure a logical path to success in your Bitcoin Wealth Building journey."
                  : "A tracking tool to “Track Your Progress”, ensuring a logical path to success in your Bitcoin Wealth Building journey."}
              </div>
            </div>
          </LightPurpleCard>
        </div>
      </div>
    </div>
  );
};
